import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Package, TrendingUp, ShoppingCart, Users, ChevronLeft, ChevronRight,
    CheckCircle, Clock, Warehouse, Truck, Check, XCircle, FileText,
    RefreshCw, AlertTriangle, AlertCircle, ChevronDown, ChevronUp,
    ArrowUpRight, ArrowDownRight, Minus, BarChart2, Zap, CreditCard,
    Calendar, Target
} from 'lucide-react';
import { statusConfig } from '../../../config/orderStatus';
import { getDashboardSummary, getTenantStats, handleApiError } from '../../../services/api/dashboardApi';
import * as salesOrderApi from '../../../services/api/salesOrderApi';
import { getFollowUpReminders } from '../../../services/api/dailySalesReportApi';
import { getMySalesTargetProgress } from '../../../services/api/salesTargetApi';
import { useAuth } from '../../../context/AuthContext';
import WarehouseRecentOrders from './warehouse/WarehouseRecentOrders';
import { TABS } from './sales/SalesOrdersView';
import { formatDateTime, timeAgo } from '../../../utils/dateUtils';

// ─── Utilities ──────────────────────────────────────────────────────────────

function formatCurrency(value) {
    if (value == null) return 'AED 0';
    return new Intl.NumberFormat('en-AE', {
        style: 'currency', currency: 'AED', minimumFractionDigits: 0, maximumFractionDigits: 0
    }).format(value);
}

function getAvatarGradient(name = '') {
    const palette = [
        'from-violet-500 to-purple-600',
        'from-blue-500 to-cyan-500',
        'from-emerald-500 to-teal-500',
        'from-orange-500 to-amber-500',
        'from-rose-500 to-pink-500',
        'from-sky-500 to-indigo-500',
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return palette[Math.abs(hash) % palette.length];
}

// ─── Sub-components ──────────────────────────────────────────────────────────

/** Top-row KPI stat card with trend arrow */
const KpiCard = ({ title, value, subtitle, subtitleBadge, icon: Icon, gradient, loading, trend, trendLabel }) => {
    const valStr = String(value || '');
    const len = valStr.length;

    let textSizeClass = 'text-base min-[380px]:text-lg sm:text-2xl';
    if (len > 15) {
        textSizeClass = 'text-[11px] min-[380px]:text-xs sm:text-lg';
    } else if (len > 12) {
        textSizeClass = 'text-xs min-[380px]:text-sm sm:text-xl';
    } else if (len > 9) {
        textSizeClass = 'text-sm min-[380px]:text-base sm:text-2xl';
    }

    return (
        <div className="bg-[var(--color-bg-card)] rounded-2xl border border-[var(--color-border)] shadow-xs p-3 sm:p-4 flex flex-col justify-between hover:shadow-md transition-all min-w-0">
            <div className="flex items-center justify-between gap-1.5 mb-1 sm:mb-2">
                <p className="text-[10px] sm:text-[11px] font-bold text-[var(--color-text-muted)] uppercase tracking-wider leading-none truncate flex-1" title={title}>
                    {title}
                </p>
                <div className={`w-7 h-7 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl ${gradient} flex items-center justify-center flex-shrink-0 shadow-xs`}>
                    <Icon className="w-3.5 h-3.5 sm:w-5 sm:h-5 text-white" />
                </div>
            </div>
            <div className="min-w-0">
                {loading ? (
                    <div className="h-6 w-20 bg-[var(--color-border)] animate-pulse rounded mt-1" />
                ) : (
                    <p className={`${textSizeClass} font-black text-[var(--color-text-primary)] leading-tight tracking-tight break-words`} title={valStr}>
                        {value}
                    </p>
                )}
                <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                    {trend != null && !loading && (
                        <span className={`flex items-center gap-0.5 text-[10px] font-bold ${trend > 0 ? 'text-emerald-500' : trend < 0 ? 'text-rose-500' : 'text-[var(--color-text-muted)]'}`}>
                            {trend > 0 ? <ArrowUpRight size={11} /> : trend < 0 ? <ArrowDownRight size={11} /> : <Minus size={11} />}
                            {Math.abs(trend)}%
                        </span>
                    )}
                    {subtitleBadge && !loading && (
                        <span className="text-[9px] font-bold bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 px-1.5 py-0.5 rounded-full">
                            {subtitleBadge}
                        </span>
                    )}
                    {subtitle && !loading && (
                        <p className="text-[9px] sm:text-[11px] text-[var(--color-text-muted)] font-medium truncate">{subtitle}</p>
                    )}
                </div>
            </div>
        </div>
    );
};

/** Compact stage chip (order pipeline) */
const StageChip = ({ title, value, icon: Icon, colorClass, loading, onClick }) => (
    <div onClick={onClick}
        className={`bg-[var(--color-bg-card)] rounded-2xl border border-[var(--color-border)] shadow-sm px-3 py-2.5 flex items-center gap-2.5 transition-all h-full ${onClick ? 'cursor-pointer hover:scale-[1.02] hover:shadow-md hover:border-gray-200' : ''}`}>
        <div className={`w-8 h-8 rounded-xl ${colorClass?.bg || 'bg-gray-100'} flex items-center justify-center flex-shrink-0`}>
            <Icon size={15} className={colorClass?.icon || 'text-gray-500'} />
        </div>
        <div className="min-w-0 flex-1">
            {loading ? <div className="h-4 w-8 bg-[var(--color-border)] animate-pulse rounded" /> :
                <p className="text-lg sm:text-xl font-black text-[var(--color-text-primary)] leading-none truncate">{value}</p>}
            <p className={`text-[10px] font-bold mt-0.5 truncate ${colorClass?.text || 'text-gray-500'}`}>{title}</p>
        </div>
    </div>
);

/** Plan usage progress bar */
const PlanBar = ({ label, used, max, loading }) => {
    if (!max || max === 0) return null;
    const pct = Math.min(100, Math.round((used / max) * 100));
    const color = pct >= 95 ? 'bg-rose-500' : pct >= 80 ? 'bg-amber-500' : 'bg-emerald-500';
    return (
        <div className="flex items-center gap-2">
            <span className="text-[11px] text-[var(--color-text-secondary)] w-16 shrink-0 font-medium">{label}</span>
            <div className="flex-1 h-1.5 bg-[var(--color-border)] rounded-full overflow-hidden">
                {loading ? <div className="h-full w-1/2 bg-[var(--color-border-soft)] animate-pulse rounded-full" /> :
                    <div className={`h-full ${color} rounded-full transition-all duration-700`} style={{ width: `${pct}%` }} />}
            </div>
            {!loading && <span className={`text-[10px] font-bold w-12 text-right shrink-0 ${pct >= 95 ? 'text-rose-600' : pct >= 80 ? 'text-amber-600' : 'text-[var(--color-text-muted)]'}`}>{used}/{max}</span>}
        </div>
    );
};

/** Alert banner */
const AlertBanner = ({ alerts }) => {
    if (!alerts?.length) return null;
    return (
        <div className="flex flex-col gap-2">
            {alerts.map((alert, i) => {
                const isHigh = alert.severity === 'HIGH';
                return (
                    <div key={i} className={`flex items-start gap-3 px-4 py-3 rounded-2xl border text-sm font-medium ${isHigh ? 'bg-rose-50 border-rose-200 text-rose-800' : 'bg-amber-50 border-amber-200 text-amber-800'}`}>
                        {isHigh ? <AlertCircle size={16} className="shrink-0 mt-0.5 text-rose-500" /> : <AlertTriangle size={16} className="shrink-0 mt-0.5 text-amber-500" />}
                        <span>{alert.message}</span>
                    </div>
                );
            })}
        </div>
    );
};

/** Single order row inside a customer card */
const OrderRow = ({ order, onClick }) => {
    const statusColor = order.statusColor
        ? { background: order.statusColor + '20', color: order.statusColor }
        : null;
    return (
        <div
            onClick={onClick}
            className={`flex items-center justify-between px-3 py-2 rounded-xl transition-all ${onClick ? 'cursor-pointer hover:bg-[var(--color-bg-hover)]' : ''} border border-[var(--color-border-soft)]`}
        >
            <div className="flex items-center gap-2 min-w-0">
                <span className="text-[10px] font-mono text-[var(--color-text-muted)] shrink-0">
                    #{(order.publicId || '').slice(-6).toUpperCase()}
                </span>
                <span
                    className="px-2 py-0.5 rounded-full text-[9px] uppercase tracking-wider font-black shrink-0"
                    style={statusColor || {}}
                >
                    <span className={!statusColor ? (statusConfig[order.statusCode]?.color || 'bg-gray-100 text-gray-600') : ''}>
                        {order.statusName || order.statusCode || '—'}
                    </span>
                </span>
                {order.assignedSalesPersonName && (
                    <span className="text-[10px] text-[var(--color-text-muted)] truncate hidden sm:block">
                        {order.assignedSalesPersonName}
                    </span>
                )}
            </div>
            <span className="text-xs font-bold text-[var(--color-text-primary)] shrink-0 ml-2">
                {formatCurrency(order.totalAmount)}
            </span>
        </div>
    );
};

/** Customer card with expandable order list */
const CustomerOrderCard = ({ card, onViewOrder, defaultExpanded = false }) => {
    const [expanded, setExpanded] = useState(defaultExpanded);
    const gradient = getAvatarGradient(card.customerName);

    return (
        <div className="bg-[var(--color-bg-card)] rounded-2xl border border-[var(--color-border)] shadow-sm overflow-hidden hover:shadow-md transition-all">
            {/* Card header */}
            <div
                className="flex items-center gap-3 p-4 cursor-pointer hover:bg-[var(--color-bg-hover)] transition-all"
                onClick={() => setExpanded(e => !e)}
            >
                <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${gradient} flex items-center justify-center text-white font-black text-sm shrink-0 shadow-sm`}>
                    {card.avatarInitial}
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-[var(--color-text-primary)] truncate leading-tight">{card.customerName}</p>
                    <p className="text-[10px] text-[var(--color-text-muted)] font-medium mt-0.5">
                        {card.activeOrderCount} active order{card.activeOrderCount !== 1 ? 's' : ''} · {formatCurrency(card.totalActiveAmount)}
                    </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <span className="text-[10px] font-black bg-blue-100 text-blue-700 rounded-full px-2 py-0.5">
                        {card.activeOrderCount}
                    </span>
                    {expanded ? <ChevronUp size={14} className="text-[var(--color-text-muted)]" /> : <ChevronDown size={14} className="text-[var(--color-text-muted)]" />}
                </div>
            </div>

            {/* Expanded order list */}
            {expanded && (
                <div className="px-4 pb-4 space-y-2 border-t border-[var(--color-border-soft)] pt-3">
                    {card.orders?.map(order => (
                        <OrderRow
                            key={order.publicId}
                            order={order}
                            onClick={onViewOrder ? () => onViewOrder(order) : undefined}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

// ─── Mini sparkline (pure CSS bars, no chart lib) ────────────────────────────
const MiniSparkline = ({ data, loading }) => {
    if (loading) return <div className="h-10 bg-[var(--color-border)] animate-pulse rounded" />;
    if (!data?.length) return <div className="h-10 flex items-center justify-center text-[11px] text-[var(--color-text-muted)]">No data</div>;
    const max = Math.max(...data.map(d => d.revenue || 0), 1);
    return (
        <div className="flex items-end gap-0.5 h-10">
            {data.slice(-20).map((d, i) => {
                const h = Math.max(4, Math.round(((d.revenue || 0) / max) * 40));
                return (
                    <div
                        key={i}
                        className="flex-1 bg-gradient-to-t from-blue-500 to-cyan-400 rounded-t-sm opacity-80 hover:opacity-100 transition-all"
                        style={{ height: `${h}px` }}
                        title={`${d.date}: ${formatCurrency(d.revenue)}`}
                    />
                );
            })}
        </div>
    );
};

// ─── At Risk Orders ────────────────────────────────────────────────────────────
const AtRiskOrders = ({ riskyOrders, onViewOrder }) => {
    const [expanded, setExpanded] = useState(false);

    if (!riskyOrders || riskyOrders.length === 0) return null;

    const highRiskCount = riskyOrders.filter(o => o.riskLevel === 'HIGH').length;

    return (
        <div className="bg-rose-50 border border-rose-200 rounded-2xl w-full flex-shrink-0 animate-in fade-in slide-in-from-top-2 overflow-hidden shadow-sm">
            <button 
                onClick={() => setExpanded(!expanded)} 
                className="w-full flex items-center gap-3 p-3 text-left focus:outline-none hover:bg-rose-100 transition-colors"
                aria-expanded={expanded}
            >
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-rose-100 shrink-0">
                    <AlertTriangle size={16} className="text-rose-600" />
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-rose-900 truncate">
                        At-Risk Orders ({riskyOrders.length})
                    </p>
                    <p className="text-[11px] text-rose-700 mt-0.5 truncate font-medium">
                        {highRiskCount > 0 ? `${highRiskCount} critical warning${highRiskCount > 1 ? 's' : ''}` : 'Requires attention'}
                    </p>
                </div>
                {expanded ? <ChevronUp size={16} className="text-rose-400" /> : <ChevronDown size={16} className="text-rose-400" />}
            </button>

            {expanded && (
                <div className="grid divide-y divide-rose-100 border-t border-rose-200">
                    {riskyOrders.map(order => {
                        const isHigh = order.riskLevel === 'HIGH';
                        const dotColor = isHigh ? 'bg-rose-500' : (order.riskLevel === 'MEDIUM' ? 'bg-amber-400' : 'bg-emerald-400');
                        return (
                            <div 
                                key={order.publicId} 
                                onClick={() => onViewOrder && onViewOrder(order)}
                                className={`flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 p-3 hover:bg-white/50 cursor-pointer transition-colors ${isHigh ? 'bg-white/30' : ''}`}
                            >
                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                    <div className={`w-2 h-2 rounded-full shrink-0 ${dotColor} shadow-sm`} />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-bold text-slate-900 truncate">{order.customerName}</p>
                                        <p className="text-[11px] text-slate-500 font-medium truncate mt-0.5">{order.reason}</p>
                                    </div>
                                </div>
                                <div className="flex items-center justify-between sm:justify-end gap-3 shrink-0 ml-5 sm:ml-0">
                                    <span className={`text-[10px] font-black px-2 py-0.5 rounded-full whitespace-nowrap ${isHigh ? 'bg-rose-100 text-rose-800' : 'bg-amber-100 text-amber-800'}`}>
                                        {order.statusCode}
                                    </span>
                                    <span className="text-sm font-black text-slate-800 truncate tabular-nums">
                                        {formatCurrency(order.totalAmount)}
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

/** Follow-up reminders section */
const FollowUpReminders = ({ reminders }) => {
    const [expanded, setExpanded] = useState(false);

    if (!reminders || reminders.length === 0) return null;

    return (
        <div className="bg-indigo-50 border border-indigo-200 rounded-2xl w-full flex-shrink-0 overflow-hidden shadow-sm">
            <button 
                onClick={() => setExpanded(!expanded)} 
                className="w-full flex items-center gap-3 p-3 text-left focus:outline-none hover:bg-indigo-100 transition-colors"
                aria-expanded={expanded}
            >
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-indigo-100 shrink-0">
                    <Calendar size={16} className="text-indigo-600" />
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-indigo-900 truncate">
                        Follow-up Reminders ({reminders.length})
                    </p>
                    <p className="text-[11px] text-indigo-700 mt-0.5 truncate font-medium">
                        Upcoming tasks for the next 7 days
                    </p>
                </div>
                {expanded ? <ChevronUp size={16} className="text-indigo-400" /> : <ChevronDown size={16} className="text-indigo-400" />}
            </button>

            {expanded && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 p-3 border-t border-indigo-200">
                    {reminders.map(visit => {
                        const dateDetails = visit.followUpDetails;
                        const isToday = dateDetails && dateDetails === new Date().toISOString().split('T')[0];
                        
                        return (
                            <div 
                                key={visit.publicId} 
                                className={`flex flex-col gap-2 p-3 rounded-xl border transition-all ${isToday ? 'bg-white border-indigo-400 shadow-md ring-1 ring-indigo-400/20' : 'bg-white/50 border-indigo-100 hover:bg-white'}`}
                            >
                                <div className="flex items-center justify-between gap-2">
                                    <span className={`text-[9px] font-black px-2 py-0.5 rounded-full uppercase tracking-wider ${isToday ? 'bg-indigo-600 text-white' : 'bg-indigo-100 text-indigo-700'}`}>
                                        {visit.followUpDetails ? visit.followUpDetails.split('-').reverse().join('-') : '—'}
                                    </span>
                                    {isToday && <span className="text-[8px] font-black text-indigo-600 uppercase">Today</span>}
                                </div>
                                <div className="min-w-0">
                                    <p className="text-xs font-bold text-slate-900 truncate">{visit.clientName}</p>
                                    <p className="text-[10px] text-slate-500 font-medium line-clamp-2 mt-1">{visit.outcomeNotes || 'No notes provided'}</p>
                                </div>
                                <div className="flex items-center gap-2 mt-auto pt-1">
                                    <span className="text-[8px] font-bold text-slate-400 uppercase tracking-tighter bg-slate-100 px-1.5 py-0.5 rounded">
                                        {visit.visitType}
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

// ─── Main Dashboard Component ─────────────────────────────────────────────────

const Dashboard = ({ orders, onViewOrder, onNavigateToTab, role }) => {
    const { user } = useAuth();

    const isOwner = user?.roles?.some(r => ['TENANT_OWNER', 'ROLE_TENANT_OWNER', 'TENANT_ADMIN', 'ROLE_TENANT_ADMIN'].includes(r));
    const isWarehouseAdmin = role === 'WAREHOUSE_PERSON';
    const isManagerOrAdmin = user?.roles?.some(r =>
        ['TENANT_OWNER', 'ROLE_TENANT_OWNER', 'TENANT_ADMIN', 'ROLE_TENANT_ADMIN',
            'SALES_MANAGER', 'ROLE_SALES_MANAGER', 'SYSTEM_ADMIN', 'ROLE_SYSTEM_ADMIN'].includes(r));

    // ── State ──────────────────────────────────────────────────────────────────
    const [summary, setSummary] = useState(null);       // full owner dashboard payload
    const [legacyStats, setLegacyStats] = useState(null); // fallback for other roles
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);
    const [localOrders, setLocalOrders] = useState(orders || []);
    const [tabCounts, setTabCounts] = useState({});
    const [reminders, setReminders] = useState([]);
    const [activeOrdersExpanded, setActiveOrdersExpanded] = useState(false);
    const [recentOrdersExpanded, setRecentOrdersExpanded] = useState(false);

    // Sales target states
    const [targetProgress, setTargetProgress] = useState(null);
    const [targetMonth, setTargetMonth] = useState(() => {
        const d = new Date();
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        return `${year}-${month}`;
    });
    const [targetLoading, setTargetLoading] = useState(false);

    const fetchTargetProgress = useCallback(async (queryMonth) => {
        if (role !== 'SALES_PERSON') return;
        try {
            setTargetLoading(true);
            const progress = await getMySalesTargetProgress(queryMonth);
            setTargetProgress(progress);
        } catch (err) {
            console.error('Error fetching personal target progress:', err);
        } finally {
            setTargetLoading(false);
        }
    }, [role]);

    useEffect(() => {
        if (role === 'SALES_PERSON') {
            fetchTargetProgress(targetMonth);
        }
    }, [targetMonth, role, fetchTargetProgress]);

    useEffect(() => { setLocalOrders(orders || []); }, [orders]);

    // ── Fetch ──────────────────────────────────────────────────────────────────
    const fetchData = useCallback(async (isRefresh = false) => {
        try {
            isRefresh ? setRefreshing(true) : setLoading(true);
            setError(null);

            const [counts, followUps] = await Promise.all([
                salesOrderApi.getOrderTabCounts(),
                isWarehouseAdmin ? Promise.resolve([]) : getFollowUpReminders({ salesPersonPublicId: isOwner ? null : (user?.publicId || user?.id) }),
                isOwner ? getDashboardSummary().then(setSummary) : 
                (isWarehouseAdmin ? Promise.resolve(null) : getTenantStats().then(setLegacyStats))
            ]);
            setTabCounts(counts || {});
            setReminders(followUps || []);

            if (isWarehouseAdmin) {
                try {
                    const warehouseOrdersResult = await salesOrderApi.searchOrders({
                        statusCodes: ['SENT_TO_WAREHOUSE', 'PENDING_WAREHOUSE', 'IN_WAREHOUSE', 'CONFIRMED', 'APPROVED', 'REVISED', 'READY', 'PACKED']
                    }, { page: 0, size: 1000, sort: 'updatedAt,desc' });
                    setLocalOrders(warehouseOrdersResult.orders || []);
                } catch (e) {
                    console.error("Failed to load warehouse dashboard orders:", e);
                }
            }

            if (!isOwner) {
                fetchRecentOrders(1);
            }

            if (role === 'SALES_PERSON') {
                fetchTargetProgress(targetMonth);
            }

        } catch (err) {
            console.error('Dashboard fetch failed:', err);
            setError(handleApiError(err));
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [isOwner, isWarehouseAdmin, user]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const getTabCount = useCallback((tabId) => {
        const tab = TABS.find(t => t.id === tabId);
        if (!tab || !tabCounts) return 0;
        return tab.statuses.reduce((sum, status) => sum + (tabCounts[status] || 0), 0);
    }, [tabCounts]);

    // ── Derived counts from local orders (non-owner roles) ──────────────────
    const effectiveOrders = useMemo(() => isManagerOrAdmin
        ? localOrders
        : localOrders.filter(o =>
            (o.assignedSalesPersonPublicId || o.assignedSalesPerson?.publicId) === (user?.publicId || user?.id) ||
            (o.delegatedSalesPersonPublicId || o.delegatedSalesPerson?.publicId) === (user?.publicId || user?.id)),
        [localOrders, isManagerOrAdmin, user]);

    const warehouseTodayCounts = useMemo(() => {
        const parseUtcDate = (dateStr) => {
            if (!dateStr) return null;
            const s = String(dateStr);
            return new Date(s.endsWith('Z') ? s : s + 'Z');
        };
        const today = new Date();
        const isToday = d => {
            if (!d) return false;
            const dt = parseUtcDate(d);
            if (!dt) return false;
            return dt.getFullYear() === today.getFullYear() && dt.getMonth() === today.getMonth() && dt.getDate() === today.getDate();
        };
        return {
            pendingToday: localOrders.filter(o => ['SENT_TO_WAREHOUSE', 'PENDING_WAREHOUSE', 'IN_WAREHOUSE'].includes(o.status?.toUpperCase())).length,
            confirmedToday: localOrders.filter(o => ['CONFIRMED', 'APPROVED', 'REVISED', 'READY', 'PACKED'].includes(o.status?.toUpperCase()) && isToday(o.updatedAt)).length,
        };
    }, [localOrders]);


    // Sorted active orders for legacy table
    const statusPriority = { 'CREATED': 1, 'NEW_ORDER': 1, 'ACKNOWLEDGED': 2, 'CONFIRMED': 2, 'PENDING': 2, 'SENT_TO_WAREHOUSE': 3, 'READY': 4, 'DISPATCHED': 5, 'DESPATCHED': 5 };
    const sortedOrders = useMemo(() => [...effectiveOrders]
        .filter(o => statusPriority[o.status?.toUpperCase()] !== undefined)
        .sort((a, b) => {
            const p = (statusPriority[a.status?.toUpperCase()] || 99) - (statusPriority[b.status?.toUpperCase()] || 99);
            return p !== 0 ? p : new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
        }), [effectiveOrders]);

    const [recentOrders, setRecentOrders] = useState([]);
    const [recentOrdersLoading, setRecentOrdersLoading] = useState(false);
    const [recentOrdersPage, setRecentOrdersPage] = useState(1);
    const [recentOrdersTotal, setRecentOrdersTotal] = useState(0);
    const [recentOrdersTotalPages, setRecentOrdersTotalPages] = useState(1);
    const itemsPerPage = 10;

    const fetchRecentOrders = useCallback(async (page = 1) => {
        if (isOwner || isWarehouseAdmin) return;
        
        try {
            setRecentOrdersLoading(true);
            const result = await salesOrderApi.getActiveOrders(
                { page: page - 1, size: itemsPerPage, sort: 'createdAt,desc' }
            );
            
            setRecentOrders(result.orders || []);
            setRecentOrdersTotal(result.totalElements || 0);
            setRecentOrdersTotalPages(result.totalPages || 1);
            setRecentOrdersPage(page);
        } catch (err) {
            console.error('Failed to fetch recent orders:', err);
        } finally {
            setRecentOrdersLoading(false);
        }
    }, [isOwner, isWarehouseAdmin, itemsPerPage]);

    useEffect(() => {
        if (!isOwner && !isWarehouseAdmin) {
            fetchRecentOrders(recentOrdersPage);
        }
    }, [recentOrdersPage, fetchRecentOrders, isOwner, isWarehouseAdmin]);

    const paginatedOrders = isOwner || isWarehouseAdmin ? [] : recentOrders;
    const totalPages = isOwner || isWarehouseAdmin ? 1 : recentOrdersTotalPages;

    // ── KPI values from summary ────────────────────────────────────────────────
    const kpi = summary?.kpi;
    const plan = summary?.plan;
    const alerts = summary?.alerts || [];

    // Plan gauge — highest utilization %
    const planAlertPct = plan ? Math.max(
        plan.maxUsers > 0 ? (plan.usersUsed / plan.maxUsers) * 100 : 0,
        plan.maxProducts > 0 ? (plan.productsUsed / plan.maxProducts) * 100 : 0,
        plan.maxOrders > 0 ? (plan.ordersThisMonth / plan.maxOrders) * 100 : 0,
    ) : 0;

    // ─────────────────────────────────────────────────────────────────────────
    // RENDER
    // ─────────────────────────────────────────────────────────────────────────
    return (
        <div className="flex flex-col gap-3 w-full max-w-[1280px] mx-auto pb-6">

            {/* ── HEADER ──────────────────────────────────────────────────── */}
            <div className="flex items-center justify-between flex-shrink-0 gap-3">
                <div>
                    <h1 className="text-xl md:text-2xl font-black text-[var(--color-text-primary)]">
                        {isOwner ? 'Business Dashboard' : 'Dashboard'}
                    </h1>
                    {isOwner && (
                        <p className="text-xs text-[var(--color-text-muted)] mt-0.5 font-medium">
                            {summary?.branding?.companyName || 'Loading...'}
                        </p>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    {error && <div className="text-xs text-rose-600 bg-rose-50 px-3 py-1.5 rounded-xl border border-rose-100">{error}</div>}
                    {isOwner && (
                        <button
                            onClick={() => fetchData(true)}
                            disabled={refreshing || loading}
                            id="dashboard-refresh-btn"
                            className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold border border-[var(--color-border)] bg-[var(--color-bg-card)] text-[var(--color-text-secondary)] hover:text-blue-600 hover:border-blue-300 hover:bg-blue-50 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                            <RefreshCw size={13} className={`${refreshing ? 'animate-spin' : ''}`} />
                            {refreshing ? 'Refreshing…' : 'Refresh'}
                        </button>
                    )}
                </div>
            </div>

            {/* ── ALERTS ──────────────────────────────────────────────────── */}
            {isOwner && alerts.length > 0 && <AlertBanner alerts={alerts} />}

            {/* ── AT-RISK ORDERS ────────────────────────────────────────────── */}
            {isOwner && summary?.riskyOrders?.length > 0 && (
                <AtRiskOrders riskyOrders={summary.riskyOrders} onViewOrder={onViewOrder} />
            )}

            {/* ── FOLLOW-UP REMINDERS ────────────────────────────────────────── */}
            {reminders.length > 0 && (
                <FollowUpReminders reminders={reminders} />
            )}

            {/* ── OWNER KPI CARDS ─────────────────────────────────────────── */}
            {isOwner && !isWarehouseAdmin && (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 flex-shrink-0">
                    <KpiCard
                        title="Today's Revenue"
                        value={formatCurrency(kpi?.todayRevenue)}
                        trend={kpi?.revenueChangePercent}
                        icon={TrendingUp}
                        gradient="bg-gradient-to-br from-emerald-500 to-teal-600"
                        loading={loading}
                    />
                    <KpiCard
                        title="Active Orders"
                        value={kpi?.activeOrderCount ?? '—'}
                        subtitle={`↑ ${kpi?.ordersByStatus ? Object.values(kpi.ordersByStatus).reduce((a, b) => a + b, 0) : 0} in pipeline`}
                        icon={ShoppingCart}
                        gradient="bg-gradient-to-br from-blue-500 to-indigo-600"
                        loading={loading}
                    />
                    <KpiCard
                        title="Customers"
                        value={kpi?.totalCustomers ?? '—'}
                        subtitleBadge={kpi?.newCustomersThisMonth > 0 ? `+${kpi.newCustomersThisMonth} this month` : null}
                        icon={Users}
                        gradient="bg-gradient-to-br from-violet-500 to-purple-600"
                        loading={loading}
                    />
                    <KpiCard
                        title="Products / Plan"
                        value={kpi?.totalProducts != null && kpi?.maxProducts
                            ? `${kpi.totalProducts} / ${kpi.maxProducts}`
                            : (kpi?.totalProducts ?? '—')}
                        subtitleBadge={planAlertPct >= 80 ? `${Math.round(planAlertPct)}% used` : null}
                        icon={Package}
                        gradient={planAlertPct >= 95
                            ? 'bg-gradient-to-br from-rose-500 to-red-600'
                            : planAlertPct >= 80
                                ? 'bg-gradient-to-br from-amber-400 to-orange-500'
                                : 'bg-gradient-to-br from-sky-500 to-cyan-600'}
                        loading={loading}
                    />
                </div>
            )}

            {/* ── SALES PERSON TARGET PROGRESS WIDGET ─────────────────────── */}
            {!isOwner && !isWarehouseAdmin && role === 'SALES_PERSON' && targetProgress && (
                <div className="bg-[var(--color-bg-card)] rounded-2xl border border-[var(--color-border)] p-4 flex flex-col gap-4 shadow-sm w-full flex-shrink-0">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-xl bg-cyan-50 dark:bg-cyan-950/20 text-cyan-600 flex items-center justify-center">
                                <Target size={18} />
                            </div>
                            <div>
                                <h3 className="text-xs font-black text-[var(--color-text-primary)] uppercase tracking-wider">
                                    Monthly Sales target
                                </h3>
                                <p className="text-[10px] text-[var(--color-text-muted)] font-medium mt-0.5">
                                    Track your monthly revenue achievements
                                </p>
                            </div>
                        </div>
                        <input
                            type="month"
                            value={targetMonth}
                            onChange={(e) => setTargetMonth(e.target.value)}
                            className="self-start sm:self-auto bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-2.5 py-1.5 text-[11px] font-black text-slate-700 dark:text-slate-300 outline-none cursor-pointer"
                        />
                    </div>
                    {targetLoading ? (
                        <div className="h-12 w-full bg-[var(--color-border-soft)] animate-pulse rounded-xl" />
                    ) : targetProgress.targetAmount > 0 ? (
                        <div className="flex flex-col gap-2.5">
                            <div className="flex items-end justify-between">
                                <div>
                                    <p className="text-[9px] font-bold text-[var(--color-text-muted)] uppercase tracking-widest leading-none">Achieved / Target</p>
                                    <div className="flex items-baseline gap-1.5 mt-1">
                                        <span className="text-lg font-black text-emerald-600 dark:text-emerald-400">
                                            {new Intl.NumberFormat('en-AE', { style: 'currency', currency: 'AED', maximumFractionDigits: 0 }).format(targetProgress.achievedAmount)}
                                        </span>
                                        <span className="text-xs text-[var(--color-text-muted)] font-bold">
                                            / {new Intl.NumberFormat('en-AE', { style: 'currency', currency: 'AED', maximumFractionDigits: 0 }).format(targetProgress.targetAmount)}
                                        </span>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <span className="text-sm font-black text-cyan-600 dark:text-sky-400">{parseFloat(targetProgress.progressPercentage || 0).toFixed(1)}%</span>
                                    <p className="text-[9px] text-slate-400 font-semibold uppercase tracking-wider mt-0.5">
                                        {targetProgress.achievedAmount >= targetProgress.targetAmount 
                                            ? 'Target Achieved! 🎉' 
                                            : `${new Intl.NumberFormat('en-AE', { style: 'currency', currency: 'AED', maximumFractionDigits: 0 }).format(targetProgress.targetAmount - targetProgress.achievedAmount)} to go`}
                                    </p>
                                </div>
                            </div>
                            <div className="w-full h-2 bg-slate-100 dark:bg-slate-900 rounded-full overflow-hidden">
                                <div 
                                    className={`h-full rounded-full transition-all duration-500 ${
                                        targetProgress.progressPercentage >= 100 
                                            ? 'bg-emerald-500' 
                                            : targetProgress.progressPercentage >= 75 
                                                ? 'bg-cyan-500' 
                                                : targetProgress.progressPercentage >= 40 
                                                    ? 'bg-amber-500' 
                                                    : 'bg-rose-500'
                                    }`}
                                    style={{ width: `${Math.min(100, parseFloat(targetProgress.progressPercentage || 0))}%` }}
                                />
                            </div>
                        </div>
                    ) : (
                        <div className="bg-slate-50/50 dark:bg-slate-900/20 border border-dashed border-slate-200 dark:border-slate-800 p-4 rounded-xl text-center">
                            <p className="text-[11px] font-bold text-slate-400 dark:text-slate-500">No monthly target has been set for you yet.</p>
                        </div>
                    )}
                </div>
            )}

            {/* ── SALES PERSON KPI CARDS ──────────────────────────────────── */}
            {!isOwner && !isWarehouseAdmin && role === 'SALES_PERSON' && (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 flex-shrink-0">
                    <KpiCard
                        title="My Customers"
                        value={legacyStats?.assignedCustomers ?? (() => {
                            // Derive unique customers from assigned orders
                            const ids = new Set(effectiveOrders.map(o => o.customerPublicId || o.customer?.publicId).filter(Boolean));
                            return ids.size || '—';
                        })()}
                        subtitle="Assigned & delegated"
                        icon={Users}
                        gradient="bg-gradient-to-br from-violet-500 to-purple-600"
                        loading={loading}
                    />
                    <KpiCard
                        title="My Orders"
                        value={(legacyStats?.activeOrdersCount ?? effectiveOrders.length) || '—'}
                        subtitle="All active orders"
                        icon={ShoppingCart}
                        gradient="bg-gradient-to-br from-blue-500 to-indigo-600"
                        loading={loading}
                    />
                    <KpiCard
                        title="Pending Orders"
                        value={getTabCount('new_order') || 0}
                        subtitle="Awaiting acknowledgement"
                        icon={Clock}
                        gradient="bg-gradient-to-br from-amber-400 to-orange-500"
                        loading={loading}
                    />
                    <KpiCard
                        title="Needs Attention"
                        value={legacyStats?.needsAttentionCount ?? (() => {
                            return effectiveOrders.filter(o =>
                                ['REJECTED', 'REQUEST_FOR_MODIFICATION'].includes(o.status?.toUpperCase() || o.statusCode?.toUpperCase())
                            ).length || 0;
                        })()}
                        subtitle="Rejected or needs revision"
                        icon={AlertTriangle}
                        gradient="bg-gradient-to-br from-rose-500 to-red-600"
                        loading={loading}
                    />
                </div>
            )}

            {/* ── NON-OWNER (MANAGER/ADMIN) LEGACY STAT ROW ───────────────── */}
            {!isOwner && !isWarehouseAdmin && role !== 'SALES_PERSON' && legacyStats && (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 flex-shrink-0">
                    <KpiCard title="Customers" value={legacyStats.totalCustomers} icon={Users} gradient="bg-gradient-to-br from-blue-500 to-blue-600" loading={loading} />
                    <KpiCard title="Products" value={legacyStats.totalProducts} icon={Package} gradient="bg-gradient-to-br from-purple-500 to-violet-600" loading={loading} />
                    <KpiCard title="Orders" value={legacyStats.totalOrders} icon={ShoppingCart} gradient="bg-gradient-to-br from-amber-400 to-orange-500" loading={loading} />
                    <KpiCard title="Revenue" value={formatCurrency(legacyStats.totalRevenue)} icon={TrendingUp} gradient="bg-gradient-to-br from-emerald-500 to-teal-600" loading={loading} />
                </div>
            )}

            {/* ── STAGE CHIPS / WAREHOUSE TODAY ───────────────────────────── */}
            {isWarehouseAdmin ? (
                <div className="grid grid-cols-2 gap-3 flex-shrink-0">
                    <StageChip title="Pending Today" value={warehouseTodayCounts.pendingToday} icon={Clock} colorClass={{ text: 'text-orange-600', bg: 'bg-orange-50', icon: 'text-orange-500' }} />
                    <StageChip title="Confirmed Today" value={warehouseTodayCounts.confirmedToday} icon={CheckCircle} colorClass={{ text: 'text-emerald-600', bg: 'bg-emerald-50', icon: 'text-emerald-600' }} />
                </div>
            ) : (
                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 sm:gap-2.5 flex-shrink-0">
                    <StageChip title="New" value={getTabCount('new_order')} icon={FileText} colorClass={{ text: 'text-blue-600', bg: 'bg-blue-50', icon: 'text-blue-600' }} onClick={() => onNavigateToTab?.('new_order')} loading={loading && isOwner} />
                    <StageChip title="Acknowledged" value={getTabCount('acknowledged')} icon={Check} colorClass={{ text: 'text-indigo-600', bg: 'bg-indigo-50', icon: 'text-indigo-600' }} onClick={() => onNavigateToTab?.('acknowledged')} loading={loading && isOwner} />
                    <StageChip title="Warehouse" value={getTabCount('warehouse')} icon={Warehouse} colorClass={{ text: 'text-orange-500', bg: 'bg-orange-50', icon: 'text-orange-500' }} onClick={() => onNavigateToTab?.('warehouse')} loading={loading && isOwner} />
                    <StageChip title="Ready" value={getTabCount('ready')} icon={Package} colorClass={{ text: 'text-teal-600', bg: 'bg-teal-50', icon: 'text-teal-600' }} onClick={() => onNavigateToTab?.('ready')} loading={loading && isOwner} />
                    <StageChip title="In Delivery" value={getTabCount('in_delivery')} icon={Truck} colorClass={{ text: 'text-cyan-600', bg: 'bg-cyan-50', icon: 'text-cyan-600' }} onClick={() => onNavigateToTab?.('in_delivery')} loading={loading && isOwner} />
                    <StageChip title="Delivered" value={getTabCount('completed')} icon={CheckCircle} colorClass={{ text: 'text-emerald-600', bg: 'bg-emerald-50', icon: 'text-emerald-600' }} onClick={() => onNavigateToTab?.('completed')} loading={loading && isOwner} />
                    <div className="col-span-2 sm:col-span-1">
                        <StageChip title="Cancelled" value={getTabCount('cancelled')} icon={XCircle} colorClass={{ text: 'text-rose-600', bg: 'bg-rose-50', icon: 'text-rose-600' }} onClick={() => onNavigateToTab?.('cancelled')} loading={loading && isOwner} />
                    </div>
                </div>
            )}

            {/* ── OWNER: 2-COLUMN PANEL (Customer Cards + Sidebar) ────────── */}
            {isOwner && !isWarehouseAdmin && (
                <div className="flex flex-col lg:grid lg:grid-cols-4 gap-3">

                    {/* LEFT: Active Orders by Customer ────────────────────── */}
                    <div className="lg:col-span-3 flex flex-col">
                        <div className="bg-[var(--color-bg-card)] rounded-2xl border border-[var(--color-border)] shadow-sm flex flex-col overflow-hidden">
                            <button
                                onClick={() => setActiveOrdersExpanded(!activeOrdersExpanded)}
                                className={`w-full px-5 py-3 flex items-center justify-between text-left focus:outline-none hover:bg-[var(--color-bg-hover)] transition-colors ${activeOrdersExpanded ? 'border-b border-[var(--color-border-soft)]' : ''}`}
                                aria-expanded={activeOrdersExpanded}
                            >
                                <h2 className="text-sm font-bold text-[var(--color-text-primary)] flex items-center gap-2">
                                    <span>Active Orders by Customer</span>
                                    {!loading && summary?.activeOrdersByCustomer && (
                                        <span className="text-xs font-medium text-[var(--color-text-muted)]">
                                            ({summary.activeOrdersByCustomer.length} customers)
                                        </span>
                                    )}
                                </h2>
                                <div className="flex items-center gap-3 shrink-0">
                                    <span className="text-[10px] text-[var(--color-text-muted)] font-medium hidden sm:block">Non-terminal orders</span>
                                    {activeOrdersExpanded ? <ChevronUp size={16} className="text-[var(--color-text-muted)]" /> : <ChevronDown size={16} className="text-[var(--color-text-muted)]" />}
                                </div>
                            </button>

                            {/* Cards list */}
                            {activeOrdersExpanded && (
                                <div className="p-3 space-y-2">
                                    {loading ? (
                                        Array.from({ length: 3 }).map((_, i) => (
                                            <div key={i} className="h-16 rounded-2xl bg-[var(--color-border)] animate-pulse" />
                                        ))
                                    ) : summary?.activeOrdersByCustomer?.length > 0 ? (
                                        summary.activeOrdersByCustomer.map(card => (
                                            <CustomerOrderCard
                                                key={card.customerPublicId}
                                                card={card}
                                                onViewOrder={onViewOrder}
                                                defaultExpanded={summary.activeOrdersByCustomer.length === 1}
                                            />
                                        ))
                                    ) : (
                                        <div className="flex flex-col items-center justify-center py-12 text-[var(--color-text-muted)]">
                                            <CheckCircle size={36} className="text-emerald-400 mb-3" />
                                            <p className="text-sm font-bold">All caught up!</p>
                                            <p className="text-xs mt-1">No active orders at the moment.</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* RIGHT: Revenue Trend + Plan Health ─────────────────── */}
                    <div className="lg:col-span-1 flex flex-col gap-3">

                        {/* Revenue Trend */}
                        <div className="bg-[var(--color-bg-card)] rounded-2xl border border-[var(--color-border)] shadow-sm p-4">
                            <div className="flex items-center justify-between mb-3">
                                <div>
                                    <p className="text-[10px] font-bold text-[var(--color-text-muted)] uppercase tracking-widest">Total Delivered Revenue</p>
                                    {loading ? <div className="h-6 w-28 bg-[var(--color-border)] animate-pulse rounded mt-1" /> :
                                        <p className="text-xl font-black text-[var(--color-text-primary)] mt-0.5">{formatCurrency(kpi?.totalDeliveredRevenue)}</p>}
                                </div>
                                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-sm">
                                    <BarChart2 size={16} className="text-white" />
                                </div>
                            </div>
                            <p className="text-[10px] text-[var(--color-text-muted)] mb-2 font-medium">Last 30 days (DELIVERED only)</p>
                            <MiniSparkline data={summary?.revenueTrend} loading={loading} />
                        </div>

                        {/* Sales Leaderboard */}
                        {summary?.topSalesPersons?.length > 0 && (
                            <div className="bg-[var(--color-bg-card)] rounded-2xl border border-[var(--color-border)] shadow-sm p-4">
                                <div className="flex items-center gap-2 mb-3">
                                    <Zap size={14} className="text-amber-500" />
                                    <p className="text-[11px] font-bold text-[var(--color-text-primary)] uppercase tracking-wider flex-1">Sales Leaderboard</p>
                                </div>

                                {/* Column headers */}
                                <div className="flex items-center gap-2 pb-1.5 mb-1 border-b border-[var(--color-border-soft)]">
                                    <div className="flex-1" /> {/* name column spacer */}
                                    <span className="text-[9px] font-bold text-[var(--color-text-muted)] uppercase tracking-wider w-12 text-center shrink-0">Active</span>
                                    <span className="text-[9px] font-bold text-emerald-600 uppercase tracking-wider w-16 text-right shrink-0">Delivered</span>
                                </div>

                                <div className="space-y-2">
                                    {summary.topSalesPersons.map((sp, idx) => (
                                        <div key={sp.publicId} className="flex items-center gap-2">
                                            {/* Rank */}
                                            <span className="text-[10px] font-black text-[var(--color-text-muted)] w-4 shrink-0 text-center">
                                                #{idx + 1}
                                            </span>

                                            {/* Avatar */}
                                            {sp.profilePictureUrl ? (
                                                <img src={sp.profilePictureUrl} alt={sp.name}
                                                    className="w-6 h-6 rounded-full object-cover shrink-0" />
                                            ) : (
                                                <div className={`w-6 h-6 rounded-full bg-gradient-to-br ${getAvatarGradient(sp.name)} flex items-center justify-center text-white text-[9px] font-black shrink-0`}>
                                                    {sp.name?.charAt(0)?.toUpperCase()}
                                                </div>
                                            )}

                                            {/* Name */}
                                            <p className="text-[11px] font-bold text-[var(--color-text-primary)] truncate flex-1 min-w-0">
                                                {sp.name}
                                            </p>

                                            {/* Active orders count badge */}
                                            <span className="w-12 text-center shrink-0">
                                                {sp.activeOrderCount > 0 ? (
                                                    <span className="text-[10px] font-bold text-blue-600 bg-blue-50 rounded-full px-1.5 py-0.5">
                                                        {sp.activeOrderCount}
                                                    </span>
                                                ) : (
                                                    <span className="text-[10px] text-[var(--color-text-muted)]">—</span>
                                                )}
                                            </span>

                                            {/* Delivered revenue */}
                                            <span className="w-16 text-right text-[10px] font-bold text-emerald-600 shrink-0 tabular-nums">
                                                {sp.deliveredRevenue > 0
                                                    ? formatCurrency(sp.deliveredRevenue)
                                                    : <span className="text-[var(--color-text-muted)] font-normal">AED 0</span>
                                                }
                                            </span>
                                        </div>
                                    ))}
                                </div>

                                {/* Legend */}
                                <div className="flex items-center gap-3 mt-3 pt-2 border-t border-[var(--color-border-soft)]">
                                    <div className="flex items-center gap-1">
                                        <span className="w-2 h-2 rounded-full bg-blue-500 shrink-0" />
                                        <span className="text-[9px] text-[var(--color-text-muted)]">Active orders</span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" />
                                        <span className="text-[9px] text-[var(--color-text-muted)]">Delivered revenue</span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Plan Health */}
                        {plan && (
                            <div className="bg-[var(--color-bg-card)] rounded-2xl border border-[var(--color-border)] shadow-sm p-4">
                                <div className="flex items-center gap-2 mb-3">
                                    <CreditCard size={14} className="text-blue-500" />
                                    <p className="text-[11px] font-bold text-[var(--color-text-primary)] uppercase tracking-wider">Plan: {plan.planName}</p>
                                </div>
                                <div className="space-y-2">
                                    <PlanBar label="Users" used={plan.usersUsed} max={plan.maxUsers} loading={loading} />
                                    <PlanBar label="Products" used={plan.productsUsed} max={plan.maxProducts} loading={loading} />
                                    <PlanBar label="Orders" used={plan.ordersThisMonth} max={plan.maxOrders} loading={loading} />
                                </div>
                                {plan.subscriptionEndDate && (
                                    <p className="text-[10px] text-[var(--color-text-muted)] mt-3 font-medium">
                                        Renews: {new Date(plan.subscriptionEndDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                                    </p>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ── WAREHOUSE / NON-OWNER: ORDERS TABLE ─────────────────────── */}
            {isWarehouseAdmin ? (
                <div className="mt-2 flex flex-col flex-1 min-h-0">
                    <WarehouseRecentOrders
                        standalone
                        sortOrder="desc"
                        onViewOrder={(o) => onViewOrder && onViewOrder(o)}
                        onOrdersRefresh={setLocalOrders}
                    />
                </div>
            ) : !isOwner && (
                <div className="bg-[var(--color-bg-card)] rounded-2xl shadow-sm border border-[var(--color-border)] overflow-hidden mt-2 flex flex-col flex-1 min-h-0">
                    <button
                        onClick={() => setRecentOrdersExpanded(!recentOrdersExpanded)}
                        className={`w-full px-5 py-3 flex items-center justify-between text-left focus:outline-none hover:bg-[var(--color-bg-hover)] transition-colors ${recentOrdersExpanded ? 'border-b border-[var(--color-border-soft)]' : ''}`}
                        aria-expanded={recentOrdersExpanded}
                    >
                        <h2 className="text-base font-bold text-[var(--color-text-primary)]">
                            Recent Orders <span className="text-xs font-medium text-[var(--color-text-muted)]">(Active)</span>
                        </h2>
                        {recentOrdersExpanded ? <ChevronUp size={16} className="text-[var(--color-text-muted)]" /> : <ChevronDown size={16} className="text-[var(--color-text-muted)]" />}
                    </button>
                    {recentOrdersExpanded && (
                        <>
                            <div className="overflow-auto flex-1 animate-fade-in">
                                <table className="w-full text-left border-collapse">
                                    <thead className="sticky top-0 z-10">
                                        <tr className="bg-[var(--color-bg-secondary)]">
                                            <th className="px-4 py-2.5 text-[10px] font-bold text-[var(--color-text-muted)] uppercase tracking-wider">Order ID</th>
                                            <th className="px-4 py-2.5 text-[10px] font-bold text-[var(--color-text-muted)] uppercase tracking-wider">Customer & City</th>
                                            <th className="px-4 py-2.5 text-[10px] font-bold text-[var(--color-text-muted)] uppercase tracking-wider">Date</th>
                                            <th className="px-4 py-2.5 text-[10px] font-bold text-[var(--color-text-muted)] uppercase tracking-wider">Status</th>
                                            <th className="px-4 py-2.5 text-[10px] font-bold text-[var(--color-text-muted)] uppercase tracking-wider">Assignee</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-[var(--color-border-soft)]">
                                        {recentOrdersLoading ? (
                                            Array.from({ length: 3 }).map((_, i) => (
                                                <tr key={i} className="animate-pulse">
                                                    <td colSpan="5" className="px-4 py-3"><div className="h-4 bg-[var(--color-border)] rounded w-3/4"></div></td>
                                                </tr>
                                            ))
                                        ) : paginatedOrders.map(order => (
                                            <tr key={order.id} onClick={() => onViewOrder && onViewOrder(order)}
                                                className={`hover:bg-[var(--color-bg-hover)] transition-colors ${onViewOrder ? 'cursor-pointer' : ''}`}>
                                                <td className="px-4 py-3">
                                                    <span className="text-xs font-mono font-bold text-blue-600">
                                                        {order.orderRef || `#${(order.publicId || '').slice(-6).toUpperCase()}`}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <div className="flex items-center gap-3">
                                                        <div className={`w-8 h-8 min-w-[32px] rounded-full bg-gradient-to-br ${getAvatarGradient(order.customer?.name)} flex items-center justify-center text-white font-bold text-xs shrink-0`}>
                                                            {order.customer?.name?.charAt(0)?.toUpperCase() || '?'}
                                                        </div>
                                                        <div className="min-w-0">
                                                            <p className="text-sm font-semibold text-[var(--color-text-primary)] truncate">{order.customer?.name || 'Unknown'}</p>
                                                            <p className="text-[10px] text-[var(--color-text-muted)] font-medium truncate">{order.customer?.city || order.customer?.address || '—'}</p>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    <p className="text-[11px] text-[var(--color-text-secondary)] font-medium">
                                                        {formatDateTime(order.createdAt || order.orderDate)}
                                                    </p>
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    <span className={`px-2 py-0.5 rounded-md text-[11px] font-bold ${statusConfig[order.status]?.color || 'bg-gray-100 text-gray-600'}`}>
                                                        {statusConfig[order.status]?.label || order.status}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    <p className="text-sm text-[var(--color-text-secondary)]">
                                                        {order.assignedSalesPerson?.firstName && order.assignedSalesPerson?.lastName
                                                            ? `${order.assignedSalesPerson.firstName} ${order.assignedSalesPerson.lastName}`
                                                            : order.assignedSalesPersonName || order.assignedSalesPerson?.firstName || order.assignedSalesPerson?.name || 'Unassigned'}
                                                    </p>
                                                </td>
                                            </tr>
                                        ))}
                                        {paginatedOrders.length === 0 && !recentOrdersLoading && (
                                            <tr><td colSpan="5" className="px-4 py-10 text-center text-[var(--color-text-muted)] text-sm">No active orders</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            {totalPages > 1 && (
                                <div className="px-5 py-3 border-t border-[var(--color-border-soft)] flex items-center justify-between text-sm bg-[var(--color-bg-card)] flex-shrink-0">
                                    <span className="text-xs text-[var(--color-text-muted)]">
                                        Showing <b className="text-[var(--color-text-primary)]">{(recentOrdersPage - 1) * itemsPerPage + 1}</b>–<b className="text-[var(--color-text-primary)]">{Math.min(recentOrdersPage * itemsPerPage, recentOrdersTotal)}</b> of <b className="text-[var(--color-text-primary)]">{recentOrdersTotal}</b>
                                    </span>
                                    <div className="flex items-center gap-1">
                                        <button onClick={() => setRecentOrdersPage(p => Math.max(1, p - 1))} disabled={recentOrdersPage === 1 || recentOrdersLoading}
                                            className={`p-1.5 rounded-lg border ${recentOrdersPage === 1 ? 'border-[var(--color-border)] text-[var(--color-text-muted)]' : 'border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)]'} transition-all`}>
                                            <ChevronLeft className="w-4 h-4" />
                                        </button>
                                        <button onClick={() => setRecentOrdersPage(p => Math.min(totalPages, p + 1))} disabled={recentOrdersPage === totalPages || recentOrdersLoading}
                                            className={`p-1.5 rounded-lg border ${recentOrdersPage === totalPages ? 'border-[var(--color-border)] text-[var(--color-text-muted)]' : 'border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)]'} transition-all`}>
                                            <ChevronRight className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default Dashboard;
